<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <!-- Print Preview Section -->
    <div class="max-w-4xl mx-auto space-y-8">
      <!-- Colored Mandala -->
      <div v-if="printDefaultMandala === 'true'" class="pagebreak">
        <div class="bg-white rounded-lg shadow-sm p-4 md:p-8 text-center">
          <h2 class="payload text-xl md:text-2xl text-gray-800 mb-4 md:mb-6">{{ payload }}</h2>
          <img
            v-if="b64image_color"
            :src="'data:image/jpeg;base64,' + b64image_color"
            class="printEl mx-auto max-w-full h-auto"
            alt="Colored Mandala"
          />
        </div>
      </div>

      <!-- Black and White with Numbers -->
      <div v-if="printOnlyDigits === 'true'" class="pagebreak">
        <div class="digits bg-white rounded-lg shadow-sm p-4 md:p-8 text-center">
          <h2 class="payload text-xl md:text-2xl text-gray-800 mb-4 md:mb-6">{{ payload }}</h2>
          <img
            v-if="b64image_bw"
            :src="'data:image/jpeg;base64,' + b64image_bw"
            class="printEl mx-auto max-w-full h-auto"
            alt="Black and White Mandala"
          />

          <!-- Color Legend for digits mode - show without v-if for debug -->
          <div class="mt-8 color-legend">
            <h3 class="text-lg font-semibold text-gray-700 mb-4">{{ $t('message.colorLegend') }}</h3>
            <div v-if="paletteLegend.length > 0" class="flex flex-wrap justify-center gap-3">
              <div
                v-for="color in paletteLegend"
                :key="color.id"
                class="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 shadow-sm"
              >
                <!-- Use thick colored border instead of background for reliable printing -->
                <div
                  class="w-10 h-10 rounded-full"
                  :style="{
                    border: '8px solid ' + color.hex,
                    backgroundColor: '#fff'
                  }"
                ></div>
                <span class="text-lg font-bold text-gray-800">
                  {{ color.id }}
                </span>
              </div>
            </div>
            <div v-else class="text-sm text-gray-500">
              Loading palette...
            </div>
          </div>
        </div>
      </div>

      <!-- Black and White with Dots -->
      <div v-if="printOnlyDots === 'true'" class="pagebreak">
        <div class="bg-white rounded-lg shadow-sm p-4 md:p-8 text-center">
          <h2 class="payload text-xl md:text-2xl text-gray-800 mb-4 md:mb-6">{{ payload }}</h2>
          <img
            v-if="b64image_bw_dots"
            :src="'data:image/jpeg;base64,' + b64image_bw_dots"
            class="printEl mx-auto max-w-full h-auto"
            alt="Dotted Mandala"
          />
        </div>
      </div>

      <!-- Individual Pattern Pages -->
      <div v-if="printDotsByNumbers === 'true'" class="pagebreak">
        <template v-for="index in patternSorted" :key="index">
          <div class="pagebreak">
            <div class="bg-white rounded-lg shadow-sm p-4 md:p-8 text-center">
              <h2 class="payload text-xl md:text-2xl text-gray-800 mb-4 md:mb-6">
                {{ payload }} №{{ index }}
              </h2>
              <img
                v-if="b64images_dotted[index]"
                :src="b64images_dotted[index]"
                class="printEl mx-auto max-w-full h-auto"
                alt="Pattern Mandala"
              />
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Print Dialog Modal -->
    <TransitionRoot appear :show="modaltogle" as="template">
      <Dialog as="div" @close="closemodal" class="relative z-50">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/25" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel class="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900">
                  {{ $t("message.print") }}
                </DialogTitle>

                <div class="mt-4">
                  <p class="text-sm text-gray-500">
                    {{ $t("message.printHint") }}
                  </p>
                </div>

                <div class="mt-6 flex justify-end">
                  <button
                    type="button"
                    class="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    @click="closemodal"
                  >
                    {{ $t("message.close") }}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script>
import { onBeforeUnmount } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue';
import axiosInstance, { DRAW_API_BASE } from '@/axios/axios';

import { encodeQueryData } from '@/utils.js';

export default {
  name: "Print-comp",
  components: {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionRoot,
    TransitionChild
  },

  data() {
    return {
      payload: "",
      b64image_color: "",
      b64image_bw: "",
      b64image_bw_dots: "",
      b64images_dotted: [],
      main_matrix: [],
      shapeSelected: "",
      shapeSelectedId: "",
      patternSymbols: "",
      patternUnfiltered: "",
      paletteSelected: 9,
      palettes: {},
      paletteLegend: [],
      firstMethodSelected: "",
      additionalMethodSelected: "",
      pattern: "",
      printDefaultMandala: false,
      printOnlyDigits: false,
      printOnlyDots: false,
      modaltogle: false,
      patternLenSelected: 0,
      patternSorted: [],
      draw_url: "",
    };
  },

  setup() {
    onBeforeUnmount(() => {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    });
  },

  mounted() {
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    this.parseQueryString();
    this.getShapeData()
    this.loadImages();
    this.unColorizeShape();
    this.colorizeShape();
    this.DotShape();
  },
  methods: {
    getShapeData() {
      // Fetch both shapesdata and commondata (for palettes)
      Promise.all([
        axiosInstance.get(`${DRAW_API_BASE}free/shapesdata/`, {
          params: { shape: this.shapeSelected, q: this.payload }
        }),
        axiosInstance.get(`${DRAW_API_BASE}free/commondata/`)
      ])
      .then(([shapesResp, commonResp]) => {
        const shapeData = shapesResp.data;
        const commonData = commonResp.data;

        console.log('Raw commondata response:', commonData);
        console.log('commonData.palettes:', commonData.palettes);
        console.log('shapeData.palettes:', shapeData.palettes);

        // API returns string keys "9", "12", "13" - convert to number keys for easier access
        this.main_matrix = {};
        this.palettes = {};

        // Convert main_matrix keys to numbers
        for (const [key, value] of Object.entries(shapeData.main_matrix || {})) {
          this.main_matrix[parseInt(key)] = value;
        }

        // Palettes might be in shapeData (from /shapesdata/) or commonData (from /commondata/)
        const palettesSource = shapeData.palettes || commonData.palettes || {};
        console.log('Palettes source:', palettesSource);

        for (const [key, value] of Object.entries(palettesSource)) {
          this.palettes[parseInt(key)] = value;
        }

        console.log('Converted main_matrix keys:', Object.keys(this.main_matrix));
        console.log('Converted palettes keys:', Object.keys(this.palettes));

        // Get pattern symbols like Phrase.vue: main_matrix[paletteSelected][length - patternLenSelected]
        const paletteIdx = parseInt(this.paletteSelected);
        const matrix = this.main_matrix[paletteIdx];
        console.log('Matrix for palette', paletteIdx, ':', matrix);
        if (matrix && matrix.length) {
          const symbolIndex = matrix.length - this.patternLenSelected;
          this.patternSymbols = matrix[symbolIndex] || [];
          console.log('Pattern symbols at index', symbolIndex, ':', this.patternSymbols);
        } else {
          this.patternSymbols = [];
        }
        this.buildPaletteLegend();
      })
      .catch(err => {
        console.error('Error loading shape data:', err);
      });
    },
    buildPaletteLegend() {
      const legend = [];
      if (!this.patternSymbols || this.patternSymbols.length === 0) {
        console.warn('No pattern symbols available');
        this.paletteLegend = legend;
        return;
      }

      if (!this.palettes) {
        console.warn('Palettes not loaded');
        this.paletteLegend = legend;
        return;
      }

      const pattern_first_symbol = this.patternSymbols[0] < 2 ? 1 : this.patternSymbols[0];
      const paletteIdx = parseInt(this.paletteSelected);

      console.log('Building legend:', {
        pattern_first_symbol,
        paletteSelected: paletteIdx,
        hasPalettes: !!this.palettes,
        palettesKeys: Object.keys(this.palettes)
      });

      // Access palettes[paletteSelected][pattern_first_symbol]
      const paletteGroup = this.palettes[paletteIdx];
      console.log('Palette group:', paletteIdx, paletteGroup);

      if (!paletteGroup) {
        console.warn('No palette group for index:', paletteIdx);
        this.paletteLegend = legend;
        return;
      }

      const temp_palette = paletteGroup[pattern_first_symbol];
      console.log('Temp palette for pattern_first_symbol', pattern_first_symbol, ':', temp_palette);

      if (temp_palette) {
        for (let i = 1; i <= 7; i++) {
          if (temp_palette[i]) {
            legend.push({ id: i, hex: temp_palette[i] });
          }
        }
        for (let i = 8; i <= paletteIdx; i++) {
          if (temp_palette[i]) {
            legend.push({ id: i, hex: temp_palette[i] });
          }
        }
      }
      this.paletteLegend = legend;
      console.log('Palette legend built:', this.paletteLegend);
    },
    async closemodal() {
      this.modaltogle = false;

      // Wait for modal transition to complete (300ms) plus a small buffer
      await new Promise(resolve => setTimeout(resolve, 400));

      // Show print dialog
      this.printDownload();
    },
    beforeUnloadHandler(event) {
      this.closePrintDialog();
    },

    closePrintDialog() {
      if (window.matchMedia('print').matches) {
        window.close();
      }
    },

    parseQueryString() {
      const required = [
        "payload",
        "shapeSelected",
        "shapeSelectedId",
        "patternLenSelected",
        "paletteSelected",
        "firstMethodSelected",
        "additionalMethodSelected",
        "printDefaultMandala",
        "printOnlyDigits",
        "printOnlyDots",
        "printDotsByNumbers",
        "pattern"
      ];

      required.forEach(param => {
        if (this.$route.query[param] !== undefined) {
          this[param] = this.$route.query[param];
        }
      });

      // Convert string params to numbers
      this.patternLenSelected = parseInt(this.patternLenSelected) || 1;
      this.paletteSelected = parseInt(this.paletteSelected) || 9;

      this.draw_url = `${DRAW_API_BASE}premium/shape/${this.shapeSelected}?`;
      this.patternSorted = this.pattern.split(',').map(Number).sort((a, b) => a - b);
    },

    async sendDrawRequest(data) {
      try {
        const url = this.draw_url + encodeQueryData(data);
        const response = await axiosInstance.get(url);
        return response?.data?.pic || "";
      } catch (error) {
        console.error('Draw request failed:', error);
        return "";
      }
    },

    async colorizeShape() {
      const data = {
        q: this.payload,
        patternLength: this.patternLenSelected,
        palette_id: this.paletteSelected,
        first_method: this.firstMethodSelected,
        additional_method: this.additionalMethodSelected,
        colorize: 1,
      };
      this.b64image_color = await this.sendDrawRequest(data);
    },

    async unColorizeShape() {
      const data = {
        q: this.payload,
        patternLength: this.patternLenSelected,
        palette_id: this.paletteSelected,
        first_method: this.firstMethodSelected,
        additional_method: this.additionalMethodSelected,
        colorize: 0,
        filter: Array.from({length: 20}, (_, i) => i + 1).join(',')
      };
      const img = await this.sendDrawRequest(data);
      this.b64image_bw = img;
    },

    async filterShape(index) {
      const data = {
        q: this.payload,
        patternLength: this.patternLenSelected,
        palette_id: this.paletteSelected,
        first_method: this.firstMethodSelected,
        additional_method: this.additionalMethodSelected,
        colorize: 0,
        num_to_dot: 1,
        filter: index,
      };
      return await this.sendDrawRequest(data);
    },
    async DotShape() {
      const data = {
        q: this.payload,
        patternLength: this.patternLenSelected,
        palette_id: this.paletteSelected,
        first_method: this.firstMethodSelected,
        additional_method: this.additionalMethodSelected,
        colorize: 0,
        num_to_dot: 1,
        filter: this.pattern
      };
      const img = await this.sendDrawRequest(data);
      this.b64image_bw_dots = img;
    },
    async loadImages() {
      try {
            const promises = [];
            for (let i = 0; i < this.patternSorted.length; i++) {
                let ind = this.patternSorted[i];
                promises.push(this.filterShape(ind));
            }
            const results = await Promise.all(promises);

            results.forEach((result, i) => {
                let ind = this.patternSorted[i];
                this.b64images_dotted[ind] = 'data:image/png;base64,' + result;
            });

            await new Promise(resolve => setTimeout(resolve, 3000));
            this.openModal();
        } catch (error) {
              console.error('Error fetching images:', error);
        }
    },

    printDownload() {
      setTimeout(() => window.print(), 0);
    },

    openModal() {
      this.modaltogle = true;
    },
  },
};
</script>

<style>
@media print {
  /* Force portrait orientation */
  @page {
    size: portrait;
    margin: 1cm;
  }

  body {
    margin: 0;
    padding: 0;
  }

  .pagebreak {
    clear: both;
    page-break-after: always;
  }

  .pagebreak:last-child {
    page-break-after: auto;
  }

  /* Make image fit within page while maintaining aspect ratio */
  .printEl,
  .digits img {
    max-width: 95vw !important;
    max-height: 70vh !important;
    width: auto !important;
    height: auto !important;
    object-fit: contain !important;
  }

  /* Ensure all mandala images fit */
  img {
    max-width: 95vw !important;
    max-height: 65vh !important;
    object-fit: contain !important;
  }

  .color-legend {
    page-break-inside: avoid;
  }

  /* Force background colors to print */
  .color-legend div[style*="background-color"] {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  /* Force borders to print */
  .color-legend div[class*="border"] {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}

.payload {
  font-size: clamp(18px, 2vw, 24px);
}
</style>
